import { useEffect } from "react";
import { useUiStore } from "@/application/stores/uiStore";
import type { ActiveReaction } from "@/application/usecases/useCompanionCoach";
import type { Hint } from "@/domain/learning/model/hint";
import { playVoice, stopVoice } from "@/infrastructure/assets/audio";
import { type CompanionExpression, companionAssetPath } from "@/infrastructure/assets/paths";
import { Button } from "@/presentation/components/Button";
import { FloatZone } from "@/presentation/components/layout/Zones";
import "./Companion.css";

export interface CompanionProps {
  readonly activeReaction: ActiveReaction | null;
  readonly hints: readonly Hint[];
  readonly revealedHintCount: number;
  readonly canRequestHint: boolean;
  readonly onRequestHint: () => void;
}

const DEFAULT_EXPRESSION: CompanionExpression = "neutral";

/**
 * 師範代ノーマの常駐ウィジェット（Z4。ui-design.md §5.7 / D-109）。
 * 待機中は一切動かない（表情の差し替えは声かけが起きた時のみ）。吹き出しは左へ開く。
 * 助言（`hints`）は手数メーターの近くに置かない、という制約を満たすため
 * DrillViewの外側・この位置に独立して置く（F-007「助言はペナルティなし」）。
 */
export function Companion({
  activeReaction,
  hints,
  revealedHintCount,
  canRequestHint,
  onRequestHint,
}: CompanionProps) {
  const lastLine = activeReaction?.reaction.lines.at(-1);
  const expression =
    (lastLine?.expression as CompanionExpression | undefined) ?? DEFAULT_EXPRESSION;
  const revealedHints = hints.slice(0, revealedHintCount);
  const muted = useUiStore((state) => state.muted);
  const volume = useUiStore((state) => state.volume);

  useEffect(() => {
    if (!activeReaction || muted) {
      stopVoice();
      return undefined;
    }
    const src = activeReaction.voiceSrc;
    const timer = window.setTimeout(() => {
      playVoice(src, false, volume);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      stopVoice();
    };
  }, [activeReaction, muted, volume]);

  return (
    <FloatZone className="companion">
      <div className="companion__stage">
        {activeReaction && (
          // `key`に声かけのIDを与え、新しい台詞のたびに立ち上がりの動きを出す
          // （前の台詞が残り続けて「いつ言われたのか分からない」状態を作らないため）。
          <div key={activeReaction.id} className="companion__bubble" aria-live="polite">
            {activeReaction.reaction.lines.map((line, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: 1つの声かけ内でセリフの並びは不変で、一意なIDも持たない
              <p key={index} className="companion__bubble-line">
                {line.text}
              </p>
            ))}
          </div>
        )}
        <img
          className="companion__portrait"
          src={companionAssetPath(expression)}
          alt=""
          aria-hidden="true"
        />
      </div>

      {hints.length > 0 && (
        <div className="companion__hints">
          {revealedHints.length > 0 && (
            <ul className="companion__hint-list">
              {revealedHints.map((hint, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: 助言は段階（配列の並び）自体が意味を持ち、常に先頭からのみ増える
                <li key={index} className="companion__hint-item">
                  {hint}
                </li>
              ))}
            </ul>
          )}
          {canRequestHint && (
            <Button variant="quiet" className="companion__hint-btn" onClick={onRequestHint}>
              師範代に聞く（残り {hints.length - revealedHintCount}）
            </Button>
          )}
        </div>
      )}
    </FloatZone>
  );
}
